import { HeadContent, Scripts, useLocation } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { preload } from "react-dom";
import { ThemeProvider } from "next-themes";
import { AppStoreBanner } from "@/components/app/shell/app-store-banner";
import { Toaster } from "@/components/ui/sonner";
import { isPeopleHost } from "@/lib/host/host-rewrite";
import { exposeFileHook } from "@/lib/links/in-app";
import { getSiteVersion } from "@/lib/marketing/site-version";
import { currentHost } from "@/lib/host/request";
import { STALE_BUILD_SCRIPT } from "@/lib/host/stale-build";
import { useHeadingTitle } from "@/lib/workspace/use-heading-title";
import { ThemeColourSync } from "@/routes/-components/theme-colour-sync";

const RouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

// /v2 is dark by design, whatever the stored theme. Decided on the document: portalled menus and
// :root aliases sit outside any wrapper.
function useDarkArm(): { dark: boolean; marketing: boolean } {
  const pathname = useLocation({ select: (location) => location.pathname });
  const marketing = getSiteVersion(pathname) === "v2";
  return { dark: marketing, marketing };
}

// Toggled by hand: a changed `className` rewrites the attribute and wipes the reader's `ac-pal-*`
// palette.
function useArmClass(darkArm: boolean): string | undefined {
  const [rendered] = useState(darkArm);
  useLayoutEffect(() => {
    document.documentElement.classList.toggle("people-dark", darkArm);
  }, [darkArm]);
  return rendered ? "people-dark" : undefined;
}

// GTM on marketing pages only: the signed-in product shares the domain and holds health
// information. The container carries GA4 and the Meta Pixel, so add neither separately (page views
// double).
const GTM_CONTAINER_ID = "GTM-MMGN5N4X";

// Loader fetched on first tap, key or scroll, or 4 s after load; a visitor gone within ~4 s is not
// counted.
const GTM_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var done=false,E=['pointerdown','keydown','scroll','touchstart'];function g(){if(done)return;done=true;E.forEach(function(e){w.removeEventListener(e,g,true)});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);}E.forEach(function(e){w.addEventListener(e,g,{capture:true,passive:true,once:true})});function q(){setTimeout(g,4000);}if(d.readyState==='complete')q();else w.addEventListener('load',q);})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`;

function useTagManager(marketingArm: boolean): boolean {
  const host = currentHost();
  return marketingArm && host !== undefined && isPeopleHost(host);
}

export function RootDocument({ children }: { children: ReactNode }) {
  // Via react-dom's preload, not route `links`: a route link lands after ~60 script preloads and
  // waits on a slow phone.
  preload("/fonts/archivo-latin.woff2", {
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  });
  const { dark: darkArm, marketing } = useDarkArm();
  const armClass = useArmClass(darkArm);
  const tagManager = useTagManager(marketing);
  useEffect(exposeFileHook, []);
  useHeadingTitle();
  return (
    <html lang="en" className={armClass} suppressHydrationWarning>
      <head>
        {tagManager ? (
          <script
            // eslint-disable-next-line react/no-danger -- Google's fixed snippet, built from our container id
            dangerouslySetInnerHTML={{ __html: GTM_SCRIPT }}
          />
        ) : null}
        {/* Not in the route's `links`, which React's head management drops. */}
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="manifest" href="/manifest.webmanifest" />
        <link rel="apple-touch-icon" href="/app-icon/apple-touch-icon.png" />
        <HeadContent />
        <AppStoreBanner />
      </head>
      <body>
        {tagManager ? (
          <noscript>
            {/* No sandbox: a sandboxed frame records no page view. */}
            {/* eslint-disable-next-line react/iframe-missing-sandbox */}
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
              title="Google Tag Manager"
            />
          </noscript>
        ) : null}
        {/* Before paint: a palette applied in an effect shows the default accent for a frame. */}
        <script
          // eslint-disable-next-line react/no-danger -- a fixed script of ours; it reads storage, writes a class
          dangerouslySetInnerHTML={{
            // Unreadable storage leaves the default palette.
            __html: `try{var p=localStorage.getItem("ac-palette");if(p&&p!=="indigo")document.documentElement.classList.add("ac-pal-"+p)}catch(e){}`,
          }}
        />
        {/* Before the app's scripts, so a tab open across a deploy reloads onto the new build
            (lib/host/stale-build.ts). */}
        <script
          // eslint-disable-next-line react/no-danger -- a fixed script of ours (lib/host/stale-build.ts)
          dangerouslySetInnerHTML={{ __html: STALE_BUILD_SCRIPT }}
        />
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          enableSystem={false}
          storageKey="ac-theme"
          forcedTheme={darkArm ? "dark" : undefined}
        >
          {children}
          <Toaster />
          {/* Remounts when the marketing arm (its own page colour) comes or goes. */}
          <ThemeColourSync key={marketing ? "marketing" : "app"} />
        </ThemeProvider>
        <Suspense>
          <RouterDevtools />
        </Suspense>
        <Scripts />
      </body>
    </html>
  );
}
